/**
 * crop-geometry — the single source of truth for framing math.
 *
 * Given a source image, a target frame ratio, a focal point and a zoom, it returns
 * the source-rect to draw into the frame. The post-preview (CSS transform) and the
 * publish-time variant bake BOTH consume this, so the preview can never promise a
 * crop the publisher ignores (the "is this real?" trust bug). Pure + unit-tested.
 *
 * Conventions:
 *  - `frameRatio` = target frame aspect = width / height (1 = square, 0.8 = 4:5,
 *    1.91 = landscape). Use `ratioToNumber(token)` from placement-matrix to derive it.
 *  - `focalX` / `focalY` ∈ [0,1] = where in the SOURCE the frame centres (0.5 = centre).
 *  - `zoom` ≥ 1; 1 = "fill" (the largest frame-ratio window that fits the source —
 *    cover, never letterbox). >1 zooms in (smaller window). Values < 1 are treated as 1.
 */

export interface CropInput {
  /** Native source width in px. */
  sourceW: number;
  /** Native source height in px. */
  sourceH: number;
  /** Target frame aspect = width / height. */
  frameRatio: number;
  /** Horizontal focal point in the source, 0..1. Default 0.5 (centre). */
  focalX?: number;
  /** Vertical focal point in the source, 0..1. Default 0.5 (centre). */
  focalY?: number;
  /** Zoom factor, ≥ 1. Default 1 (fill). */
  zoom?: number;
}

/** The source-rect to draw into the frame (in source pixels). */
export interface CropRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

export const DEFAULT_FOCAL = 0.5;
export const MIN_ZOOM = 1;

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(max, Math.max(min, value));
}

/**
 * The largest `frameRatio`-shaped window that fits the source (cover/fill) at zoom 1,
 * then shrunk by `zoom`, positioned by the focal point and clamped so the window can
 * never leave the source (no gaps at the frame edge — the IG fill invariant).
 */
export function computeCrop(input: CropInput): CropRect {
  const { sourceW, sourceH, frameRatio } = input;
  const focalX = clamp(input.focalX ?? DEFAULT_FOCAL, 0, 1);
  const focalY = clamp(input.focalY ?? DEFAULT_FOCAL, 0, 1);
  const zoom = Math.max(MIN_ZOOM, input.zoom ?? MIN_ZOOM);

  const sourceRatio = sourceW / sourceH;

  // Cover: pick the largest frame-ratio rect that fits inside the source.
  let baseW: number;
  let baseH: number;
  if (sourceRatio > frameRatio) {
    // Source is wider than the frame → height-bound; crop the sides.
    baseH = sourceH;
    baseW = sourceH * frameRatio;
  } else {
    // Source is taller/narrower → width-bound; crop top/bottom.
    baseW = sourceW;
    baseH = sourceW / frameRatio;
  }

  // Zoom in = a smaller window into the source.
  const sw = baseW / zoom;
  const sh = baseH / zoom;

  // Centre the window on the focal point, clamped fully inside the source.
  const sx = clamp(focalX * sourceW - sw / 2, 0, sourceW - sw);
  const sy = clamp(focalY * sourceH - sh / 2, 0, sourceH - sh);

  return { sx, sy, sw, sh };
}

export interface MaxZoomInput {
  sourceW: number;
  sourceH: number;
  frameRatio: number;
  /** Rendered frame width in CSS px (the displayed size). */
  renderedFrameW: number;
}

/**
 * The zoom at which the drawn source-window equals the rendered frame pixels 1:1 —
 * i.e. the most you can zoom before the image upscales and blurs. Never below 1
 * (a tiny source still fills; it just has no zoom headroom).
 */
export function computeMaxZoom({
  sourceW,
  sourceH,
  frameRatio,
  renderedFrameW,
}: MaxZoomInput): number {
  if (renderedFrameW <= 0) return MIN_ZOOM;
  const sourceRatio = sourceW / sourceH;
  // Width of the cover window at zoom 1 (see computeCrop).
  const baseW = sourceRatio > frameRatio ? sourceH * frameRatio : sourceW;
  // At zoom z the window width is baseW / z; it hits the rendered width when
  // baseW / z === renderedFrameW → z = baseW / renderedFrameW.
  return Math.max(MIN_ZOOM, baseW / renderedFrameW);
}

/** Clamp a zoom value into [1, max]. */
export function clampZoom(zoom: number, max: number): number {
  return clamp(zoom, MIN_ZOOM, Math.max(MIN_ZOOM, max));
}
